import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, Mail, MessageSquare, Tv } from 'lucide-react';
import { useState } from 'react';

import { adminFetch } from '../lib/api';

interface SubRow {
  id: number;
  order_id: number;
  lead_id: number;
  plan_id: number;
  iptv_username: string | null;
  iptv_panel_url: string | null;
  iptv_provider: string | null;
  connections: number;
  started_at: string | null;
  expires_at: string;
  status: string;
  // Joined columns from `_enrich_subscriptions` in the backend.
  lead_email: string | null;
  lead_name: string | null;
  lead_whatsapp: string | null;
  lead_country_iso: string | null;
  order_ref: string | null;
  order_status: string | null;
  order_total_cents: number | null;
  order_currency: string | null;
  plan_code: string | null;
  plan_name: string | null;
  plan_duration_days: number | null;
  m3u_url: string | null;
}

function statusBadge(status: string) {
  if (status === 'active') return <Badge variant="success">{status}</Badge>;
  if (status === 'pending_provision') return <Badge variant="warning">{status}</Badge>;
  if (status === 'expired') return <Badge variant="destructive">{status}</Badge>;
  if (status === 'cancelled') return <Badge variant="destructive">{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function orderBadge(status: string | null) {
  if (!status) return null;
  if (status === 'paid') return <Badge variant="success">{status}</Badge>;
  if (status === 'awaiting_payment') return <Badge variant="warning">{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function formatPrice(cents: number | null, currency: string | null): string {
  if (cents == null || !currency) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${currency} ${(cents / 100).toFixed(2)}`;
  }
}

function copyToClipboard(text: string): void {
  navigator.clipboard?.writeText(text).catch(() => {});
}

export function SubscriptionsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SubRow | null>(null);
  const [viewing, setViewing] = useState<SubRow | null>(null);
  const [creds, setCreds] = useState({ username: '', password: '', panel_url: '' });

  const subs = useQuery({
    queryKey: ['admin', 'subscriptions', 'all'],
    queryFn: () => adminFetch<SubRow[]>('/api/v1/admin/subscriptions', { query: { limit: 200 } }),
  });

  const setCredsMutation = useMutation({
    mutationFn: (id: number) =>
      adminFetch(`/api/v1/admin/subscriptions/${id}/credentials`, {
        method: 'POST',
        body: {
          iptv_username: creds.username,
          iptv_password: creds.password,
          iptv_panel_url: creds.panel_url,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      setEditing(null);
      setCreds({ username: '', password: '', panel_url: '' });
    },
  });

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2">IPTV</th>
              <th className="px-3 py-2 text-center">Conn.</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subs.isPending && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {subs.data?.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-muted-foreground">
                  No subscriptions yet.
                </td>
              </tr>
            )}
            {subs.data?.map((s) => (
              <tr key={s.id} className="border-t align-top">
                <td className="px-3 py-2 text-xs text-muted-foreground">#{s.id}</td>
                <td className="px-3 py-2">
                  {/* Customer block — email is the primary identifier;
                      name + WhatsApp + country shown below as supporting
                      detail so the operator can act on each lead from one
                      row without opening the lead dialog. */}
                  <div className="space-y-0.5">
                    <div className="font-medium">{s.lead_email ?? '—'}</div>
                    {(s.lead_name || s.lead_country_iso) && (
                      <div className="text-xs text-muted-foreground">
                        {s.lead_name ?? ''}
                        {s.lead_name && s.lead_country_iso ? ' · ' : ''}
                        {s.lead_country_iso ?? ''}
                      </div>
                    )}
                    {s.lead_whatsapp && (
                      <div className="text-[11px] text-muted-foreground">
                        WhatsApp:{' '}
                        <a
                          href={`https://wa.me/${s.lead_whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 underline hover:text-emerald-700"
                        >
                          {s.lead_whatsapp}
                        </a>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {/* Order linkage — ref, status, amount. Lets the operator
                      jump-correlate to the order without leaving this view. */}
                  <div className="space-y-0.5">
                    <div className="font-mono text-xs">{s.order_ref ?? `#${s.order_id}`}</div>
                    <div className="flex items-center gap-1.5">{orderBadge(s.order_status)}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {formatPrice(s.order_total_cents, s.order_currency)}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="space-y-0.5">
                    <div className="text-sm">{s.plan_name ?? s.plan_code ?? '—'}</div>
                    {s.plan_duration_days != null && (
                      <div className="text-[11px] text-muted-foreground">
                        {s.plan_duration_days} days
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {/* IPTV access block — username + provider chip + a small
                      "Copy M3U" CTA so the operator can paste the playlist
                      URL into a support reply without opening the detail
                      dialog. */}
                  {s.iptv_username ? (
                    <div className="space-y-1">
                      <div className="inline-flex items-center gap-1.5 font-mono text-xs">
                        <Tv className="h-3 w-3 text-indigo-600" />
                        {s.iptv_username}
                      </div>
                      {s.iptv_provider && (
                        <div>
                          <Badge variant="secondary" className="text-[10px] uppercase">
                            {s.iptv_provider}
                          </Badge>
                        </div>
                      )}
                      {s.m3u_url && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(s.m3u_url!)}
                          className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800"
                          title="Copy M3U playlist URL"
                        >
                          <Copy className="h-3 w-3" /> Copy M3U
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">{s.connections}</td>
                <td className="px-3 py-2 text-xs">{new Date(s.expires_at).toLocaleDateString()}</td>
                <td className="px-3 py-2">{statusBadge(s.status)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setViewing(s)}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    {s.status === 'pending_provision' && (
                      <Button size="sm" variant="outline" onClick={() => setEditing(s)}>
                        Set creds
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail dialog — read-only view of everything we know about a
          single subscription. Big M3U URL textarea + copy button so
          support can resend access. */}
      <Dialog open={viewing !== null} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Subscription #{viewing?.id}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm">
              <div className="grid gap-3 md:grid-cols-2">
                <FieldRO label="Customer" value={viewing.lead_email} />
                <FieldRO label="Name" value={viewing.lead_name} />
                <FieldRO label="WhatsApp" value={viewing.lead_whatsapp} />
                <FieldRO label="Country" value={viewing.lead_country_iso} />
                <FieldRO label="Order" value={viewing.order_ref ?? `#${viewing.order_id}`} />
                <FieldRO
                  label="Amount"
                  value={formatPrice(viewing.order_total_cents, viewing.order_currency)}
                />
                <FieldRO
                  label="Plan"
                  value={
                    viewing.plan_name && viewing.plan_duration_days != null
                      ? `${viewing.plan_name} (${viewing.plan_duration_days}d)`
                      : (viewing.plan_code ?? '—')
                  }
                />
                <FieldRO label="Connections" value={String(viewing.connections)} />
                <FieldRO label="Provider" value={viewing.iptv_provider} />
                <FieldRO label="Username" value={viewing.iptv_username} mono />
                <FieldRO label="Started" value={viewing.started_at?.slice(0, 10) ?? null} />
                <FieldRO label="Expires" value={viewing.expires_at.slice(0, 10)} />
              </div>

              {/* M3U playlist block — the one the customer needs. Big,
                  copyable, with a one-click Copy button. */}
              <div className="space-y-1.5">
                <Label>M3U playlist URL</Label>
                <textarea
                  readOnly
                  value={viewing.m3u_url ?? ''}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-secondary/30 px-3 py-2 font-mono text-[11px] focus:outline-none"
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!viewing.m3u_url}
                    onClick={() => viewing.m3u_url && copyToClipboard(viewing.m3u_url)}
                  >
                    <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy M3U
                  </Button>
                  {viewing.iptv_panel_url && (
                    <a
                      href={viewing.iptv_panel_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-600 underline hover:text-blue-800"
                    >
                      Open panel
                    </a>
                  )}
                  {viewing.lead_email && (
                    <a
                      href={`mailto:${viewing.lead_email}`}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <Mail className="h-3 w-3" /> Email customer
                    </a>
                  )}
                  {viewing.lead_whatsapp && (
                    <a
                      href={`https://wa.me/${viewing.lead_whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${viewing.lead_name ?? ''}, your IPTV M3U playlist: ${viewing.m3u_url ?? ''}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-800"
                    >
                      <MessageSquare className="h-3 w-3" /> Send on WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set credentials for sub #{editing?.id}</DialogTitle>
          </DialogHeader>
          {editing && (
            <p className="mb-2 text-xs text-muted-foreground">
              Customer: {editing.lead_email ?? '—'} · Order:{' '}
              {editing.order_ref ?? `#${editing.order_id}`}
            </p>
          )}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={creds.username}
                onChange={(e) => setCreds({ ...creds, username: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                value={creds.password}
                onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="panel">Panel URL</Label>
              <Input
                id="panel"
                value={creds.panel_url}
                onChange={(e) => setCreds({ ...creds, panel_url: e.target.value })}
              />
            </div>
            <Button
              className="w-full"
              disabled={setCredsMutation.isPending}
              onClick={() => editing && setCredsMutation.mutate(editing.id)}
            >
              {setCredsMutation.isPending ? 'Saving…' : 'Save & deliver'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldRO({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? 'font-mono text-xs' : ''}>{value ?? '—'}</div>
    </div>
  );
}
